/*----------------------------------
- HOME: STAGE
----------------------------------*/

/**
 * Home's first screen (`div#top.stage`): the hero card on the world (the exported scene, Home's LCP), the copy on the
 * picture's foot with the shipped h1 on one line per sentence, the persona calls to action and the members line, the
 * saucer that crosses the sky now and then, and the demo window whose top half sits in the card. The picture is the
 * one the page's floating dock chose (`hero`, Day station or Night launch, Home v3, 2026-11-04; the dock itself sits
 * outside the stage, in `LandingExperience`); `data-hero` on the stage carries it to the sheet, which swaps the sand
 * colour under the picture with it.
 *
 * @docs docs/features/website-landing-experience
 * @adr  ADR-0110
 * @rule The hero picture is the only eager image of the page (`HeroScene priority="hero"`); no film, no motion scene.
 *       The server renders Day station; Night launch is fetched only when a visitor picks it.
 * @rule The members line reads the server's `totalSignups`; a zero hides it, never shows "0 have joined".
 */

/*----------------------------------
- DEPENDANCES
----------------------------------*/

import { HeroScene } from '@uniquedomains/components/marketing/hero-scene/HeroScene';
import { ProofLine } from '@uniquedomains/components/marketing/proof-line/ProofLine';
import type { HomePersona } from '@uniquedomains/components/marketing/demo-window/PersonaSwitch';

import { HOME_HERO_COPY } from '@/client/catalogs/landing/home/homeCopy';
import type { HomeDomainsPreviewData } from '@/client/pages/landing/lib/homeDomainsPreview';
import HomeDemo from './HomeDemo';
import HomeHeroCtas from './HomeHeroCtas';
import HomeSaucer from './HomeSaucer';
import { HOME_HERO_SCENES, type HomeHero } from './homeScenes';

/*----------------------------------
- TYPES
----------------------------------*/

type HomeStageProps = {
    totalSignups: number;
    totalDomains: number;
    preview: HomeDomainsPreviewData | null;
    persona: HomePersona;
    onPersonaChange: (persona: HomePersona) => void;
    /** The picture the dock shows (`useHomeHero`, owned by the page so the dock can sit outside the stage). */
    hero: HomeHero;
};

/*----------------------------------
- COMPONENT
----------------------------------*/

const HomeStage = ({ totalSignups, totalDomains, preview, persona, onPersonaChange, hero }: HomeStageProps) => {
    const { title, sub } = HOME_HERO_COPY;

    return (
        <div class="stage" id="top" data-hero={hero}>
            <HeroScene sources={HOME_HERO_SCENES[hero]} priority="hero" />
            <section class="hero">
                <div class="copy">
                    <div class="wrap">
                        <div class="l">
                            {/* `--l` staggers each line's rise (home.css `line-up`); one sentence per line. */}
                            <h1>
                                <span class="ln" style="--l:0">
                                    <span class="y">{title.lead}</span>
                                </span>
                                <small>
                                    <span class="ln" style="--l:1">
                                        <span>{title.middle}</span>
                                    </span>
                                    <span class="ln" style="--l:2">
                                        <b>{title.payoff}</b>
                                    </span>
                                </small>
                            </h1>
                        </div>
                        <div class="r">
                            <p class="sub">
                                {sub.before}
                                <span class="nw">{sub.nowrap}</span>
                                {sub.after}
                            </p>
                            <HomeHeroCtas />
                            {totalSignups > 0 ? (
                                <ProofLine count={totalSignups} label={HOME_HERO_COPY.proofLabel} data-testid="landing-hero-social-proof" />
                            ) : null}
                        </div>
                    </div>
                </div>
            </section>
            {/* The mock's `#ufo` sits here, between the hero and the demo, flying over the sky (the saucer leaf's part). */}
            <HomeSaucer />
            <HomeDemo preview={preview} totalDomains={totalDomains} persona={persona} onPersonaChange={onPersonaChange} />
        </div>
    );
};

export default HomeStage;
